import { AlertTriangle, Clock3, RefreshCw, X } from 'lucide-react'
import { Button } from '../common/Button'
import type { OfflineOperationType, OutboxOperation, OutboxStatus } from '../../services/offline/types'

interface SyncPendingDialogProps {
  open: boolean
  operations: OutboxOperation[]
  syncing: boolean
  onClose: () => void
  onRetry: () => Promise<void>
  onSync: () => Promise<void>
}

const operationLabels: Record<OfflineOperationType, string> = {
  CREATE_REQUISICAO: 'Criar requisição',
  SAVE_REQUISICAO: 'Salvar requisição',
  SAVE_COMPRA: 'Salvar compra',
  BLOCK_REQUISICAO: 'Bloquear requisição',
  DELETE_REQUISICAO: 'Excluir requisição',
  CREATE_ITEM_REQUEST: 'Solicitar cadastro de item',
}

const statusLabels: Record<OutboxStatus, string> = {
  PENDING: 'Aguardando envio',
  PROCESSING: 'Em processamento',
  NEEDS_ATTENTION: 'Requer atenção',
}

export function SyncPendingDialog({ open, operations, syncing, onClose, onRetry, onSync }: SyncPendingDialogProps) {
  if (!open) return null
  const requiresAttention = operations.some((operation) => operation.status === 'NEEDS_ATTENTION')

  return <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
    if (event.target === event.currentTarget && !syncing) onClose()
  }}>
    <section className="confirm-dialog sync-pending-dialog" role="dialog" aria-modal="true" aria-labelledby="sync-pending-title">
      <header>
        <span className="dialog-warning"><AlertTriangle aria-hidden="true" /></span>
        <div>
          <h2 id="sync-pending-title">Pendências de sincronização</h2>
          <p>Estas operações pertencem ao usuário atual neste dispositivo.</p>
        </div>
        <button className="icon-button" type="button" aria-label="Fechar" title="Fechar" disabled={syncing} onClick={onClose}><X /></button>
      </header>
      <div className="sync-pending-list">
        {operations.length === 0 ? <p className="empty-state">Nenhuma operação pendente.</p> : operations.map((operation) => <article key={operation.operationId}>
          <div className="sync-pending-title">
            {operation.status === 'NEEDS_ATTENTION' ? <AlertTriangle aria-hidden="true" /> : <Clock3 aria-hidden="true" />}
            <div>
              <strong>{operationLabels[operation.operationType]}</strong>
              <span>{operationReference(operation)}</span>
            </div>
            <span className={operation.status === 'NEEDS_ATTENTION' ? 'state-inactive' : 'state-pending'}>{statusLabels[operation.status]}</span>
          </div>
          <dl>
            <div><dt>Criada em</dt><dd>{formatDate(operation.createdAt)}</dd></div>
            <div><dt>Tentativas</dt><dd>{operation.attempts}</dd></div>
            {operation.nextAttemptAt ? <div><dt>Próxima tentativa</dt><dd>{formatDate(operation.nextAttemptAt)}</dd></div> : null}
          </dl>
          {operation.lastError ? <p className="sync-error"><strong>Motivo:</strong> {safeError(operation.lastError)}</p> : null}
        </article>)}
      </div>
      <footer>
        <Button type="button" onClick={onClose} disabled={syncing}>Fechar</Button>
        <Button type="button" variant="primary" icon={<RefreshCw />} loading={syncing} onClick={() => void (requiresAttention ? onRetry() : onSync())}>
          {requiresAttention ? 'Tentar novamente' : 'Sincronizar agora'}
        </Button>
      </footer>
    </section>
  </div>
}

function operationReference(operation: OutboxOperation): string {
  if (operation.payload.type === 'CREATE_REQUISICAO' || operation.payload.type === 'SAVE_REQUISICAO') {
    const draft = operation.payload.draft
    return draft.numero_requisicao || draft.obra || `Referência ${shortId(operation.entityId)}`
  }
  if (operation.payload.type === 'CREATE_ITEM_REQUEST') {
    return operation.payload.request.descricao || `Referência ${shortId(operation.entityId)}`
  }
  return `Requisição ${shortId(operation.entityId)}`
}

function shortId(value: string): string {
  return value.length > 12 ? value.slice(0, 8) : value
}

function safeError(value: string): string {
  return value
    .replace(/Bearer\s+\S+/gi, 'Bearer [REMOVIDO]')
    .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[TOKEN REMOVIDO]')
    .slice(0, 240)
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}
